import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Order } from '../../models/intefaces/order';
import { LoginInformation } from '../../models/intefaces/login-information';
import { TranslationsService } from '../translations/translations.service';

export enum Target {
  camera,
  web,
}

@Injectable({
  providedIn: 'root',
})
export class VerificationService {
  private readonly formData = new FormData();

  private username?: string;
  private password?: string;

  private accountIsLinked = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly translationsService: TranslationsService
  ) {}

  addValue(key: string, value: any): void {
    this.formData.append(key, value);
  }

  addSignature(data: string): void {
    this.formData.append('signature', data);
  }

  addOrder(data: Order): void {
    Object.keys(data).forEach((key) => {
      this.formData.delete(key);
      this.formData.append(key, (data as any)[key]);
    });
  }

  hasValues(keys: string[]): boolean {
    let hasValue = true;
    for (let index = 0; index < keys.length && hasValue; index++) {
      hasValue = this.formData.has(keys[index]);
    }
    return hasValue;
  }

  fill(target: Order, fields: string[]): Order {
    fields.forEach((field) => {
      if (this.hasValues([field])) {
        (target as any)[field] = this.formData.get(field);
      }
    });
    return target;
  }

  getUsername(): string {
    return this.username as string;
  }

  getPassword(): string {
    return this.password as string;
  }

  getToken(): string {
    return this.translationsService.getTranslations().session;
  }

  getApiUrl(): string {
    return this.translationsService.getTranslations().api_url;
  }

  getPartnerToken(): string {
    return this.translationsService.getTranslations().partner_token;
  }

  isLinked(): boolean {
    return this.accountIsLinked;
  }

  remove(key: string): void {
    this.formData.delete(key);
  }

  setToken(token: string): void {
    this.formData.append('token', token);
  }

  setPartnerToken(token: string): void {
    this.formData.append('partner_token', token);
  }

  send<T>(to: Target, linkToCurrentAccount = false): Observable<T> {
    this.accountIsLinked = linkToCurrentAccount;
    const target = !this.isWeb(to) ? '/request/camera' : '/api/v1/order/add';
    if (this.isWeb(to)) {
      const headers = this.getBearerHeader(linkToCurrentAccount);
      const endpoint = this.getEndpoint(target, linkToCurrentAccount);
      this.username = this.formData.get('email') as string;
      this.password = this.formData.get('password') as string;
      this.setPartnerToken(this.getPartnerToken());
      return this.sendFormData<T>(endpoint, new HttpHeaders(headers));
    }
    return this.sendFormData<T>(target);
  }

  private getBearerHeader(linkToCurrentAccount: boolean): any {
    return linkToCurrentAccount && this.isAuthenticated()
      ? { Authorization: `Bearer ${this.isAuthenticated()}` }
      : {};
  }

  private getEndpoint(target: string, linkToCurrentAccount: boolean): string {
    return linkToCurrentAccount ? `${target}Linked` : `${target}NotLinked`;
  }

  private isAuthenticated(): boolean {
    return this.translationsService.getTranslations().authenticated;
  }

  private isWeb(to: Target): boolean {
    return to === Target.web;
  }

  setAccount(loginInformation: LoginInformation): void {
    this.formData.append('lang', loginInformation.lang as string);
    this.formData.append('password', loginInformation.password);
    this.formData.append('confirm_password', loginInformation.confirmPassword);
  }

  reset(): void {
    this.formData.forEach(
      (formDataEntryValue: FormDataEntryValue, key: string) => {
        this.remove(key);
      }
    );
  }

  private sendFormData<T>(
    endpoint: string,
    headers?: HttpHeaders
  ): Observable<any> {
    const options: any = {};
    if (headers) {
      options.headers = headers;
    }

    return this.httpClient.post<T>(`${endpoint}`, this.formData, options);
  }
}
